import Title from '../../types/Title';
import { useWebReaderGalleyAccess } from '../hooks/useWebReaderGalleyAccess';
import { GhostButton } from './GhostButton';
import { Tooltip } from 'antd';
import ReviewCopyActionTooltip from './ReviewCopyActionTooltip';
import { RoundButton } from './RoundButton';
import { withIsMobile } from 'treeline-react-core-components/WindowSizeProvider';
import useWebReaderProducts from '../Titles/hooks/useWebReaderProducts';
import useLocalizeDate from '../Localization/hooks/useLocalizeDate';
import { useTranslate } from '@above-the-treeline/localization';

interface IReaderLinkProps {
	title: Title;
	buttonType: string;
	isMobile: () => boolean;
}

function EdelweissReaderLink({
	title,
	buttonType,
	isMobile,
}: IReaderLinkProps) {
	const { sku } = title;
	const { isLoading, hasWebReaderGalleyAccess } = useWebReaderGalleyAccess();
	const { translate } = useTranslate();

	const localizeDate = useLocalizeDate();
	const webReaderProducts = useWebReaderProducts();

	const getTooltipTitle = (lastAccessedDate: string): JSX.Element => {
		const { digitalCreatedDate, digitalUpdatedDate } = title;
		let header;
		if (!hasWebReaderGalleyAccess(sku)) {
			header = translate(
				'currently_unavailable_via_the_edelweiss_web_reader',
			);
			return (
				<div>
					<ReviewCopyActionTooltip
						headerText={header}
						createdDate={digitalCreatedDate}
						updatedDate={digitalUpdatedDate}
					/>
				</div>
			);
		} else if (lastAccessedDate) {
			header = translate('last_viewed_on_x', {
				x: localizeDate(lastAccessedDate) as string,
			});
		} else {
			header = translate('read_in_the_edelweiss_web_reader');
		}

		const percentRead: number = webReaderProducts.getPercentOfBookCompleted(
			sku,
		);

		return (
			<div>
				<ReviewCopyActionTooltip
					headerText={header}
					createdDate={digitalCreatedDate}
					updatedDate={digitalUpdatedDate}
				/>
				{percentRead && percentRead > 0 ? (
					<div
						style={{
							fontSize: 'smaller',
						}}>{`${percentRead}% Read`}</div>
				) : (
					<></>
				)}
			</div>
		);
	};

	const renderButton = () => {
		return (
			<div className="column-narrow">
				{buttonType === 'ghost' ? (
					<GhostButton sku={sku} translate={translate} />
				) : (
					<RoundButton
						sku={sku}
						isLoading={isLoading}
						hasWebReaderGalleyAccess={hasWebReaderGalleyAccess(sku)}
					/>
				)}
			</div>
		);
	};

	const renderButtonWithTooltip = () => {
		return (
			<>
				<Tooltip
					title={
						isLoading
							? translate('status_loading')
							: getTooltipTitle(
									webReaderProducts.getLastAccessedDate(
										title.sku,
									) ?? '',
							  )
					}
					placement={'topLeft'}>
					{renderButton()}
				</Tooltip>
			</>
		);
	};

	const render = () => {
		if (isMobile()) {
			return renderButton();
		}
		return renderButtonWithTooltip();
	};

	return render();
}

export default withIsMobile(EdelweissReaderLink);
